import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { Module } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { usePayrollParams } from "@/modules/payrolls/hooks/usePayrollParams";
import { FetchListRequest, SuccessFetchListResponse } from "@/services/api";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

interface UsePayrollDetailsProps<T> {
  fetchListFn: (
    params: FetchListRequest<T>,
  ) => Promise<SuccessFetchListResponse<T>>;
  nameSuffix?: string;
  enabled?: boolean;
}

export function usePayrollDetails<T>({
  fetchListFn,
  nameSuffix,
  enabled = true,
}: UsePayrollDetailsProps<T>) {
  const { payrollId } = usePayrollParams();
  const name = Module.Payrolls;

  const options = useCreateDataTableOptions<T>(`${name}-${nameSuffix}`);
  const queryKeyName = [name, payrollId];
  if (nameSuffix !== undefined) {
    queryKeyName.push(nameSuffix);
  }
  const queryKey = useDynamicQueryParams(queryKeyName, options);

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      fetchListFn({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [fetchListFn, options.filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<T>) => lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled,
  });

  const queryValue = useMemo(() => ({ query }), [query]);

  return {
    payrollId,
    name,
    options,
    query,
    queryValue,
  };
}
